import { 
  Dialog, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Box, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  ListItemSecondaryAction,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import CodeIcon from '@mui/icons-material/Code';
import { useHomeAttachmentsContext } from '../contexts/HomeAttachmentsContext';

interface DocumentsModalProps {
  open: boolean;
  onClose: () => void;
}

const DocumentsModal = ({ open, onClose }: DocumentsModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { 
    attachments, 
    isLoading, 
    error,
    getAttachmentUrl,
    isDocumentFile
  } = useHomeAttachmentsContext();
  
  // Filter for only document attachments
  const documentAttachments = attachments.filter(att => isDocumentFile(att.fileType));

  // Helper function to get appropriate icon for document type
  const getDocumentIcon = (fileType: string) => {
    if (fileType === 'application/pdf') {
      return <PictureAsPdfIcon />;
    } else if (fileType === 'application/msword' || 
               fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return <DescriptionIcon />;
    } else if (fileType === 'text/plain') {
      return <TextSnippetIcon />;
    } else if (fileType === 'text/markdown') {
      return <CodeIcon />;
    } else {
      return <DescriptionIcon />;
    }
  };

  // Helper function to format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Helper function to format upload date
  const formatUploadDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <Dialog
      fullScreen={isMobile}
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 2100 }}
      PaperProps={{
        sx: {
          maxHeight: isMobile ? '100%' : '80vh',
          height: isMobile ? '100%' : 'auto'
        }
      }}
    >
      <AppBar position="static" color="primary" elevation={1}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Documents ({documentAttachments.length})
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : documentAttachments.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <Typography>No documents uploaded</Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {documentAttachments.map((attachment, index) => (
              <ListItem 
                key={attachment.key} 
                sx={{ 
                  borderBottom: index < documentAttachments.length - 1 ? 1 : 0,
                  borderColor: 'divider',
                  py: 2
                }}
              >
                <ListItemIcon sx={{ mr: 2 }}>
                  {getDocumentIcon(attachment.fileType)}
                </ListItemIcon>
                <ListItemText 
                  primary={attachment.filename}
                  secondary={`${formatFileSize(attachment.size)} • ${formatUploadDate(attachment.uploaded)}`}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: 500
                    }
                  }}
                />
                <ListItemSecondaryAction sx={{ mr: 2 }}>
                  <IconButton 
                    edge="end" 
                    aria-label="download"
                    component="a"
                    href={getAttachmentUrl(attachment)}
                    download={attachment.filename}
                    sx={{ color: 'primary.main' }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Dialog>
  );
};

export default DocumentsModal;